// ============================================================================
// AdminPracticesPanel — conservation-practice catalog + practice instances
// (Build S6, Task #26). UIF only. Sidebar entry: #admin/practices.
// ============================================================================
// Two subviews toggled by local `view` state under one header:
//
//   1. CATALOG   — the ~40-code NRCS practice_def catalog. Read + edit the
//                  reference rows (name, category, unit, lifespan, active,
//                  sort). Defs also feed the create-instance picker.
//   2. INSTANCES — per-subfield practice_instance CRUD. Filter by farm +
//                  status, create new instances, edit metadata, and drive
//                  the status lifecycle (planned → in_progress → implemented
//                  → verified, with terminated / historic escapes) through
//                  server-validated transitions.
//
// Status lifecycle transitions are validated server-side; the detail modal
// only offers `allowed_transitions` and surfaces 409s (illegal_transition,
// change_reason_required) inline.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Plus, RefreshCw, AlertCircle, Edit2, X as XIcon,
  Layers, Sprout, CheckCircle2, ListChecks, ArrowRightCircle,
} from 'lucide-react';
import {
  api,
  type PracticeDef,
  type PracticeInstance,
  type PracticeStatus,
  type UnitOfMeasure,
  type TrackCode,
} from '../api';

interface Props {
  token: string;
}

const UNITS: UnitOfMeasure[] = ['acres', 'sqft', 'feet', 'count'];
const TRACKS: TrackCode[] = ['CONS', 'PROD', 'FSAF', 'MKTG'];
const PRACTICE_STATUSES: PracticeStatus[] = [
  'planned', 'in_progress', 'implemented', 'verified', 'terminated', 'historic',
];

// ----------------------------------------------------------------------------
// Formatting + tone helpers
// ----------------------------------------------------------------------------

// PracticeStatus → Tailwind pill classes.
function statusTone(status: PracticeStatus): string {
  switch (status) {
    case 'planned':     return 'bg-gray-100 border-gray-300 text-gray-700';
    case 'in_progress': return 'bg-blue-50 border-blue-200 text-blue-800';
    case 'implemented': return 'bg-amber-50 border-amber-200 text-amber-800';
    case 'verified':    return 'bg-emerald-50 border-emerald-200 text-emerald-800';
    case 'terminated':  return 'bg-red-50 border-red-200 text-red-800';
    case 'historic':    return 'bg-slate-100 border-slate-300 text-slate-600';
    default:            return 'bg-gray-100 border-gray-300 text-gray-700';
  }
}

// Amount + unit: "12 acres" when both present, bare number when unit missing,
// em-dash when the amount itself is null.
function fmtAmount(n: number | null, unit: UnitOfMeasure | null | undefined): string {
  if (n == null) return '—';
  return unit ? `${n} ${unit}` : String(n);
}

// Short date, guarded on null. (Exact format the rest of the admin uses.)
function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ISO / date string → value for a <input type="date"> (YYYY-MM-DD), or ''.
function toDateInput(s: string | null | undefined): string {
  if (!s) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return m ? m[1] : '';
}

// Instance display label: "NRCS — name" when known, else the def id.
function practiceLabel(p: PracticeInstance, defsById: Record<string, PracticeDef>): string {
  const def = defsById[p.practice_def_id];
  const nrcs = p.nrcs_code ?? def?.nrcs_code;
  const name = p.practice_name ?? def?.practice_name;
  if (nrcs && name) return `${nrcs} — ${name}`;
  if (name) return name;
  return p.practice_def_id;
}

// ============================================================================
// Panel
// ============================================================================
export function AdminPracticesPanel({ token }: Props) {
  const [view, setView] = useState<'catalog' | 'instances'>('catalog');

  // Catalog state (also feeds the create-instance modal's def picker).
  const [defs, setDefs] = useState<PracticeDef[]>([]);
  const [defsLoading, setDefsLoading] = useState(false);
  const [defsError, setDefsError] = useState('');
  const [editingDef, setEditingDef] = useState<PracticeDef | null>(null);

  // Instances state.
  const [practices, setPractices] = useState<PracticeInstance[]>([]);
  const [instLoading, setInstLoading] = useState(false);
  const [instError, setInstError] = useState('');
  const [farmIdInput, setFarmIdInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PracticeStatus>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const loadDefs = useCallback(() => {
    setDefsLoading(true);
    setDefsError('');
    api.adminPracticeDefs(token)
      .then((d) => {
        const rows = (d.practice_defs ?? []).slice().sort(
          (a, b) => a.sort_order - b.sort_order || a.nrcs_code.localeCompare(b.nrcs_code),
        );
        setDefs(rows);
      })
      .catch((err) => setDefsError(err?.message || 'Could not load the practice catalog.'))
      .finally(() => setDefsLoading(false));
  }, [token]);

  const loadInstances = useCallback(() => {
    setInstLoading(true);
    setInstError('');
    const farm = farmIdInput.trim();
    api.adminPractices(token, {
      farm_id: farm && !Number.isNaN(Number(farm)) ? Number(farm) : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    })
      .then((d) => setPractices(d.practices ?? []))
      .catch((err) => setInstError(err?.message || 'Could not load practices.'))
      .finally(() => setInstLoading(false));
  }, [token, farmIdInput, statusFilter]);

  // Catalog loads on mount (feeds both the catalog view and the create picker).
  useEffect(() => { loadDefs(); }, [loadDefs]);
  // Instances load lazily when the instances tab is active + filters change.
  useEffect(() => { if (view === 'instances') loadInstances(); }, [view, loadInstances]);

  const defsById = useMemo(() => {
    const m: Record<string, PracticeDef> = {};
    for (const d of defs) m[d.practice_def_id] = d;
    return m;
  }, [defs]);

  const activeDefs = useMemo(() => defs.filter((d) => d.is_active).length, [defs]);

  const instCounts = useMemo(() => {
    const c: Record<PracticeStatus, number> = {
      planned: 0, in_progress: 0, implemented: 0, verified: 0, terminated: 0, historic: 0,
    };
    for (const p of practices) c[p.status] = (c[p.status] ?? 0) + 1;
    return c;
  }, [practices]);

  const refreshActive = () => { if (view === 'catalog') loadDefs(); else loadInstances(); };
  const loading = view === 'catalog' ? defsLoading : instLoading;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Practices</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            The NRCS conservation-practice catalog and the per-subfield practice instances that move through the CONS track toward CPA-52.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshActive}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          {view === 'instances' && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
            >
              <Plus className="h-4 w-4" />
              Add practice
            </button>
          )}
        </div>
      </div>

      {/* Subview tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <TabButton active={view === 'catalog'} onClick={() => setView('catalog')} icon={<Layers className="h-4 w-4" />}>
          Catalog
        </TabButton>
        <TabButton active={view === 'instances'} onClick={() => setView('instances')} icon={<Sprout className="h-4 w-4" />}>
          Instances
        </TabButton>
      </div>

      {view === 'catalog' ? (
        <CatalogView
          defs={defs}
          loading={defsLoading}
          error={defsError}
          onEdit={setEditingDef}
          totalActive={activeDefs}
        />
      ) : (
        <InstancesView
          practices={practices}
          defsById={defsById}
          loading={instLoading}
          error={instError}
          counts={instCounts}
          farmIdInput={farmIdInput}
          statusFilter={statusFilter}
          onFarmIdChange={setFarmIdInput}
          onStatusChange={setStatusFilter}
          onOpenDetail={setDetailId}
        />
      )}

      {editingDef && (
        <EditDefModal
          token={token}
          def={editingDef}
          onClose={() => setEditingDef(null)}
          onSaved={() => { setEditingDef(null); loadDefs(); }}
        />
      )}
      {createOpen && (
        <CreateInstanceModal
          token={token}
          defs={defs}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); loadInstances(); }}
        />
      )}
      {detailId && (
        <DetailModal
          token={token}
          id={detailId}
          defs={defs}
          onClose={() => setDetailId(null)}
          onRefresh={loadInstances}
        />
      )}
    </div>
  );
}

// ============================================================================
// Catalog subview
// ============================================================================
function CatalogView({
  defs, loading, error, onEdit, totalActive,
}: {
  defs: PracticeDef[];
  loading: boolean;
  error: string;
  onEdit: (d: PracticeDef) => void;
  totalActive: number;
}) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Catalog codes" value={defs.length} tone="ok" icon={<Layers className="h-4 w-4" />} />
        <Kpi label="Active codes" value={totalActive} tone="ok" icon={<CheckCircle2 className="h-4 w-4" />} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">NRCS code</th>
              <th className="px-3 py-2">Practice name</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Unit</th>
              <th className="px-3 py-2 text-right">Lifespan (yrs)</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2 text-right">Sort</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-gray-500 text-center"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</td></tr>
            )}
            {!loading && defs.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-gray-500 italic text-center">No practice definitions found.</td></tr>
            )}
            {defs.map((d) => (
              <tr key={d.practice_def_id} className={'hover:bg-gray-50 ' + (d.is_active ? '' : 'opacity-50')}>
                <td className="px-3 py-2 font-mono text-gray-900">{d.nrcs_code}</td>
                <td className="px-3 py-2 font-medium text-gray-900">{d.practice_name}</td>
                <td className="px-3 py-2 text-gray-700">{d.category ?? <span className="text-gray-400 italic">—</span>}</td>
                <td className="px-3 py-2 text-gray-700">{d.unit_of_measure ?? <span className="text-gray-400 italic">—</span>}</td>
                <td className="px-3 py-2 text-right text-gray-700">{d.default_lifespan_years ?? '—'}</td>
                <td className="px-3 py-2">
                  {d.is_active
                    ? <Pill tone="ok">yes</Pill>
                    : <Pill tone="muted">no</Pill>}
                </td>
                <td className="px-3 py-2 text-right text-gray-500">{d.sort_order}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onEdit(d)}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                    title="Edit"
                    aria-label={`Edit ${d.nrcs_code}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Instances subview
// ============================================================================
function InstancesView({
  practices, defsById, loading, error, counts,
  farmIdInput, statusFilter, onFarmIdChange, onStatusChange, onOpenDetail,
}: {
  practices: PracticeInstance[];
  defsById: Record<string, PracticeDef>;
  loading: boolean;
  error: string;
  counts: Record<PracticeStatus, number>;
  farmIdInput: string;
  statusFilter: 'all' | PracticeStatus;
  onFarmIdChange: (v: string) => void;
  onStatusChange: (v: 'all' | PracticeStatus) => void;
  onOpenDetail: (id: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Loaded" value={practices.length} tone="ok" icon={<Sprout className="h-4 w-4" />} />
        <Kpi label="In progress" value={counts.in_progress} tone="amp" icon={<ListChecks className="h-4 w-4" />} />
        <Kpi label="Implemented" value={counts.implemented} tone="warn" icon={<Layers className="h-4 w-4" />} />
        <Kpi label="Verified" value={counts.verified} tone="ok" icon={<CheckCircle2 className="h-4 w-4" />} />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Farm ID</span>
          <input
            type="number"
            value={farmIdInput}
            onChange={(e) => onFarmIdChange(e.target.value)}
            placeholder="All farms"
            className="w-36 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value as 'all' | PracticeStatus)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="all">All</option>
            {PRACTICE_STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">Practice</th>
              <th className="px-3 py-2">Farm</th>
              <th className="px-3 py-2">Track</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Planned</th>
              <th className="px-3 py-2">Implemented</th>
              <th className="px-3 py-2">Planned at</th>
              <th className="px-3 py-2">Implemented at</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-gray-500 text-center"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</td></tr>
            )}
            {!loading && practices.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-gray-500 italic text-center">No practice instances match this filter.</td></tr>
            )}
            {practices.map((p) => {
              const unit = p.unit_of_measure ?? defsById[p.practice_def_id]?.unit_of_measure ?? null;
              return (
                <tr key={p.practice_instance_id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900">{practiceLabel(p, defsById)}</td>
                  <td className="px-3 py-2 text-gray-700">#{p.farm_id}</td>
                  <td className="px-3 py-2 text-gray-700">{p.track_code ?? '—'}</td>
                  <td className="px-3 py-2"><StatusPill status={p.status} /></td>
                  <td className="px-3 py-2 text-gray-700">{fmtAmount(p.planned_amount, unit)}</td>
                  <td className="px-3 py-2 text-gray-700">{fmtAmount(p.implemented_amount, unit)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(p.planned_at)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(p.implemented_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => onOpenDetail(p.practice_instance_id)}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      title="Detail & status"
                    >
                      <ArrowRightCircle className="h-3.5 w-3.5" />
                      Detail
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Edit practice-def modal
// ============================================================================
function EditDefModal({
  token, def, onClose, onSaved,
}: {
  token: string;
  def: PracticeDef;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [practiceName, setPracticeName] = useState(def.practice_name);
  const [category, setCategory] = useState(def.category ?? '');
  const [unit, setUnit] = useState<UnitOfMeasure | ''>(def.unit_of_measure ?? '');
  const [lifespan, setLifespan] = useState(def.default_lifespan_years != null ? String(def.default_lifespan_years) : '');
  const [description, setDescription] = useState(def.description ?? '');
  const [isActive, setIsActive] = useState(def.is_active);
  const [sortOrder, setSortOrder] = useState(String(def.sort_order));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;

    // Send only the fields that actually changed.
    const patch: Partial<Pick<PracticeDef,
      'practice_name' | 'category' | 'unit_of_measure' | 'default_lifespan_years' | 'description' | 'is_active' | 'sort_order'
    >> = {};

    if (practiceName !== def.practice_name) patch.practice_name = practiceName;

    const catVal = category.trim() ? category.trim() : null;
    if (catVal !== def.category) patch.category = catVal;

    const unitVal: UnitOfMeasure | null = unit || null;
    if (unitVal !== def.unit_of_measure) patch.unit_of_measure = unitVal;

    const lifeVal = lifespan.trim() ? Number(lifespan) : null;
    if (lifeVal !== def.default_lifespan_years && !(lifeVal != null && Number.isNaN(lifeVal))) {
      patch.default_lifespan_years = lifeVal;
    }

    const descVal = description.trim() ? description : null;
    if (descVal !== def.description) patch.description = descVal;

    if (isActive !== def.is_active) patch.is_active = isActive;

    const sortVal = Number(sortOrder);
    if (!Number.isNaN(sortVal) && sortVal !== def.sort_order) patch.sort_order = sortVal;

    if (Object.keys(patch).length === 0) { onClose(); return; }

    setBusy(true); setError('');
    try {
      await api.adminPracticeDefPatch(token, def.practice_def_id, patch);
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Edit ${def.nrcs_code}`} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <p className="text-[11px] text-gray-500">
          NRCS code <code className="bg-gray-100 px-1 rounded">{def.nrcs_code}</code> is fixed; edit the catalog metadata below.
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Practice name</span>
          <input type="text" required value={practiceName} onChange={(e) => setPracticeName(e.target.value)} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Category</span>
          <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Unit of measure</span>
            <select value={unit} onChange={(e) => setUnit(e.target.value as UnitOfMeasure | '')} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
              <option value="">— none —</option>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Default lifespan (yrs)</span>
            <input type="number" value={lifespan} onChange={(e) => setLifespan(e.target.value)} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={busy} />
            Active
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700">
            Sort order
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} disabled={busy}
              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
          </label>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Create practice-instance modal
// ============================================================================
function CreateInstanceModal({
  token, defs, onClose, onSaved,
}: {
  token: string;
  defs: PracticeDef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subfieldId, setSubfieldId] = useState('');
  const [farmId, setFarmId] = useState('');
  const [practiceDefId, setPracticeDefId] = useState('');
  const [trackCode, setTrackCode] = useState<TrackCode | ''>('');
  const [enrollmentId, setEnrollmentId] = useState('');
  const [programYearId, setProgramYearId] = useState('');
  const [plannedAmount, setPlannedAmount] = useState('');
  const [plannedAt, setPlannedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'planned' | 'historic'>('planned');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Prefer the defs the panel already loaded; fetch our own only if empty.
  const [localDefs, setLocalDefs] = useState<PracticeDef[]>(defs);
  useEffect(() => { if (defs.length) setLocalDefs(defs); }, [defs]);
  useEffect(() => {
    if (defs.length === 0) {
      api.adminPracticeDefs(token)
        .then((d) => setLocalDefs(
          (d.practice_defs ?? []).slice().sort((a, b) => a.sort_order - b.sort_order || a.nrcs_code.localeCompare(b.nrcs_code)),
        ))
        .catch(() => { /* def picker just stays empty; surfaced on submit */ });
    }
  }, [token, defs.length]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const subfield = Number(subfieldId);
    const farm = Number(farmId);
    if (!subfieldId.trim() || Number.isNaN(subfield) || !farmId.trim() || Number.isNaN(farm) || !practiceDefId) {
      setError('Subfield ID, Farm ID, and Practice are all required.');
      return;
    }
    setBusy(true); setError('');
    try {
      await api.adminPracticeCreate(token, {
        subfield_id: subfield,
        farm_id: farm,
        practice_def_id: practiceDefId,
        track_code: trackCode || null,
        enrollment_id: enrollmentId.trim() || null,
        program_year_id: programYearId.trim() || null,
        planned_amount: plannedAmount.trim() ? Number(plannedAmount) : null,
        planned_at: plannedAt || null,
        notes: notes.trim() || null,
        status,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Could not create practice.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Add practice instance" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Subfield ID <span className="text-red-500">*</span></span>
            <input type="number" required value={subfieldId} onChange={(e) => setSubfieldId(e.target.value)} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Farm ID <span className="text-red-500">*</span></span>
            <input type="number" required value={farmId} onChange={(e) => setFarmId(e.target.value)} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Practice <span className="text-red-500">*</span></span>
          <select required value={practiceDefId} onChange={(e) => setPracticeDefId(e.target.value)} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
            <option value="">— select a practice —</option>
            {localDefs.map((d) => (
              <option key={d.practice_def_id} value={d.practice_def_id}>{d.nrcs_code} — {d.practice_name}</option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Track</span>
            <select value={trackCode} onChange={(e) => setTrackCode(e.target.value as TrackCode | '')} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
              <option value="">— none —</option>
              {TRACKS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Initial status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as 'planned' | 'historic')} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
              <option value="planned">planned</option>
              <option value="historic">historic</option>
            </select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Planned amount</span>
            <input type="number" value={plannedAmount} onChange={(e) => setPlannedAmount(e.target.value)} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Planned at</span>
            <input type="date" value={plannedAt} onChange={(e) => setPlannedAt(e.target.value)} disabled={busy}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Enrollment ID (optional)</span>
          <input type="text" value={enrollmentId} onChange={(e) => setEnrollmentId(e.target.value)} disabled={busy} placeholder="uuid"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Program year ID (optional)</span>
          <input type="text" value={programYearId} onChange={(e) => setProgramYearId(e.target.value)} disabled={busy} placeholder="uuid"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} rows={2}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !subfieldId.trim() || !farmId.trim() || !practiceDefId}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Detail + metadata edit + status-transition modal
// ============================================================================
function DetailModal({
  token, id, defs, onClose, onRefresh,
}: {
  token: string;
  id: string;
  defs: PracticeDef[];
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [practice, setPractice] = useState<PracticeInstance | null>(null);
  const [allowed, setAllowed] = useState<PracticeStatus[]>([]);

  // Editable metadata (adminPracticePatch).
  const [trackCode, setTrackCode] = useState<TrackCode | ''>('');
  const [plannedAmount, setPlannedAmount] = useState('');
  const [implementedAmount, setImplementedAmount] = useState('');
  const [plannedAt, setPlannedAt] = useState('');
  const [implementedAt, setImplementedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [metaSaved, setMetaSaved] = useState(false);

  // Status transition (adminPracticeTransition).
  const [toStatus, setToStatus] = useState<PracticeStatus | ''>('');
  const [changeReason, setChangeReason] = useState('');
  const [tImplementedAt, setTImplementedAt] = useState('');
  const [tImplementedAmount, setTImplementedAmount] = useState('');
  const [tVerifiedAt, setTVerifiedAt] = useState('');
  const [tVerifiedBy, setTVerifiedBy] = useState('');
  const [transitioning, setTransitioning] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError('');
    api.adminPracticeDetail(token, id)
      .then((d) => {
        setPractice(d.practice);
        setAllowed(d.allowed_transitions ?? []);
        setTrackCode(d.practice.track_code ?? '');
        setPlannedAmount(d.practice.planned_amount != null ? String(d.practice.planned_amount) : '');
        setImplementedAmount(d.practice.implemented_amount != null ? String(d.practice.implemented_amount) : '');
        setPlannedAt(toDateInput(d.practice.planned_at));
        setImplementedAt(toDateInput(d.practice.implemented_at));
        setNotes(d.practice.notes ?? '');
      })
      .catch((err) => setError(err?.message || 'Could not load practice.'))
      .finally(() => setLoading(false));
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  const unit = practice
    ? (practice.unit_of_measure ?? defs.find((d) => d.practice_def_id === practice.practice_def_id)?.unit_of_measure ?? null)
    : null;

  async function saveMeta(e: React.FormEvent) {
    e.preventDefault();
    if (savingMeta) return;
    setSavingMeta(true); setError(''); setMetaSaved(false);
    try {
      await api.adminPracticePatch(token, id, {
        track_code: trackCode || null,
        planned_amount: plannedAmount.trim() ? Number(plannedAmount) : null,
        implemented_amount: implementedAmount.trim() ? Number(implementedAmount) : null,
        planned_at: plannedAt || null,
        implemented_at: implementedAt || null,
        notes: notes.trim() || null,
      });
      setMetaSaved(true);
      onRefresh();
      load();
    } catch (err: any) {
      setError(err?.message || 'Could not save metadata.');
    } finally {
      setSavingMeta(false);
    }
  }

  async function doTransition(e: React.FormEvent) {
    e.preventDefault();
    if (transitioning || !toStatus) return;
    if (toStatus === 'terminated' && !changeReason.trim()) {
      setError('A change reason is required to terminate a practice.');
      return;
    }
    setTransitioning(true); setError('');
    try {
      await api.adminPracticeTransition(token, id, {
        to_status: toStatus,
        change_reason: changeReason.trim() || null,
        ...(toStatus === 'implemented' ? {
          implemented_at: tImplementedAt || null,
          implemented_amount: tImplementedAmount.trim() ? Number(tImplementedAmount) : null,
        } : {}),
        ...(toStatus === 'verified' ? {
          verified_at: tVerifiedAt || null,
          verified_by: tVerifiedBy.trim() ? Number(tVerifiedBy) : null,
        } : {}),
      });
      onRefresh();
      onClose();
    } catch (err: any) {
      // 409s (illegal_transition, change_reason_required) surface here.
      setError(err?.message || 'Could not change status.');
    } finally {
      setTransitioning(false);
    }
  }

  return (
    <ModalShell title="Practice instance" onClose={onClose}>
      {loading ? (
        <div className="py-8 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin mr-1" />Loading…</div>
      ) : !practice ? (
        <div className="space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Close</button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Read-only header */}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900">{practiceLabel(practice, indexById(defs))}</span>
              <StatusPill status={practice.status} />
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <ReadRow label="Farm" value={`#${practice.farm_id}`} />
              <ReadRow label="Subfield" value={`#${practice.subfield_id}`} />
              <ReadRow label="Verified at" value={fmtDate(practice.verified_at)} />
              <ReadRow label="Verified by" value={practice.verified_by != null ? `#${practice.verified_by}` : '—'} />
              <ReadRow label="Enrollment" value={practice.enrollment_id ?? '—'} mono />
              <ReadRow label="Program year" value={practice.program_year_id ?? '—'} mono />
            </dl>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {/* Editable metadata */}
          <form onSubmit={saveMeta} className="space-y-3 border-t border-gray-200 pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Metadata</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Track</span>
                <select value={trackCode} onChange={(e) => setTrackCode(e.target.value as TrackCode | '')} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
                  <option value="">— none —</option>
                  {TRACKS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <div className="text-xs text-gray-500 self-end pb-2">Unit: {unit ?? '—'}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Planned amount</span>
                <input type="number" value={plannedAmount} onChange={(e) => setPlannedAmount(e.target.value)} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Implemented amount</span>
                <input type="number" value={implementedAmount} onChange={(e) => setImplementedAmount(e.target.value)} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Planned at</span>
                <input type="date" value={plannedAt} onChange={(e) => setPlannedAt(e.target.value)} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Implemented at</span>
                <input type="date" value={implementedAt} onChange={(e) => setImplementedAt(e.target.value)} disabled={savingMeta}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Notes</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={savingMeta} rows={2}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
            </label>
            <div className="flex items-center justify-end gap-2">
              {metaSaved && <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Saved</span>}
              <button type="submit" disabled={savingMeta}
                className="inline-flex items-center gap-1.5 rounded-md border border-uif-green text-uif-green-dark font-semibold px-4 py-2 text-sm hover:bg-uif-green/5 disabled:opacity-50">
                {savingMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Save metadata
              </button>
            </div>
          </form>

          {/* Status transition */}
          <form onSubmit={doTransition} className="space-y-3 border-t border-gray-200 pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Change status</h3>
            {allowed.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No transitions are available from <b>{practice.status}</b>.</p>
            ) : (
              <>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">New status</span>
                  <select value={toStatus} onChange={(e) => { setToStatus(e.target.value as PracticeStatus | ''); setError(''); }} disabled={transitioning}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none">
                    <option value="">— select new status —</option>
                    {allowed.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </label>

                {toStatus === 'implemented' && (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700 mb-1">Implemented at (optional)</span>
                      <input type="date" value={tImplementedAt} onChange={(e) => setTImplementedAt(e.target.value)} disabled={transitioning}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700 mb-1">Implemented amount (optional)</span>
                      <input type="number" value={tImplementedAmount} onChange={(e) => setTImplementedAmount(e.target.value)} disabled={transitioning}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                    </label>
                  </div>
                )}

                {toStatus === 'verified' && (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700 mb-1">Verified at</span>
                      <input type="date" value={tVerifiedAt} onChange={(e) => setTVerifiedAt(e.target.value)} disabled={transitioning}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                    </label>
                    <label className="block">
                      <span className="block text-xs font-medium text-gray-700 mb-1">Verified by (admin #)</span>
                      <input type="number" value={tVerifiedBy} onChange={(e) => setTVerifiedBy(e.target.value)} disabled={transitioning}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                    </label>
                  </div>
                )}

                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">
                    Change reason{toStatus === 'terminated' ? <span className="text-red-500"> * required to terminate</span> : ' (optional)'}
                  </span>
                  <input type="text" value={changeReason} onChange={(e) => setChangeReason(e.target.value)} disabled={transitioning}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
                </label>

                <div className="flex justify-end">
                  <button type="submit" disabled={transitioning || !toStatus}
                    className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
                    {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightCircle className="h-4 w-4" />}
                    Apply transition
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}
    </ModalShell>
  );
}

// ----------------------------------------------------------------------------
// Small shared bits
// ----------------------------------------------------------------------------

function indexById(defs: PracticeDef[]): Record<string, PracticeDef> {
  const m: Record<string, PracticeDef> = {};
  for (const d of defs) m[d.practice_def_id] = d;
  return m;
}

function ReadRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-1">
      <dt className="text-gray-500">{label}:</dt>
      <dd className={'text-gray-800 ' + (mono ? 'font-mono break-all' : '')}>{value}</dd>
    </div>
  );
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ' +
        (active
          ? 'border-uif-green text-uif-green-dark font-semibold'
          : 'border-transparent text-gray-500 hover:text-gray-700')
      }
    >
      {icon}{children}
    </button>
  );
}

function Kpi({ label, value, tone, icon }: { label: string; value: number; tone: 'ok' | 'warn' | 'amp'; icon: React.ReactNode }) {
  const cls =
    tone === 'ok'   ? 'border-emerald-100 bg-emerald-50/50'
    : tone === 'amp' ? 'border-uif-green/20 bg-uif-green/5'
    : 'border-amber-100 bg-amber-50/50';
  return (
    <div className={'rounded-lg border p-3 ' + cls}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

function StatusPill({ status }: { status: PracticeStatus }) {
  return (
    <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + statusTone(status)}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'muted'; children: React.ReactNode }) {
  const cls = tone === 'ok'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-gray-100 border-gray-300 text-gray-500';
  return <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>{children}</span>;
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
